/**
 * SharedConsentsBox – shows all of the traveler's consents.
 *
 * Sources:
 *  - GET /api/med-access/list  (MED ACCESS tokens)
 *  - GET /api/consent-requests (Doctor/Operator consent requests)
 */

import {
  CheckCircle2,
  Clock,
  ExternalLink,
  ShieldOff,
  Stethoscope,
  XCircle,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import apiClient from "../../services/api/apiClient";
import { useI18nStore } from "../../stores/i18nStore";
import type { Translation } from "../../types/i18n";

interface MedAccessToken {
  id: string;
  token: string;
  expires_at: string;
  consent_status: "PENDING" | "GRANTED" | "DENIED" | "REVOKED";
  doctor_name: string | null;
  doctor_email: string | null;
  accessed_at: string | null;
  created_at: string;
}

interface ConsentRequest {
  id: string;
  requesterName: string;
  requesterRole: string;
  requesterInstitution?: string;
  status: string;
  createdAt: string;
}

type ConsentEntry =
  | { kind: "med"; data: MedAccessToken }
  | { kind: "consent"; data: ConsentRequest };

function statusBadge(status: string, t: Translation) {
  const s = status.toUpperCase();
  if (s === "GRANTED" || s === "APPROVED")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-health-50 px-2 py-0.5 text-xs font-semibold text-health-700">
        <CheckCircle2 className="w-3 h-3" /> {t.cardxStatusGranted}
      </span>
    );
  if (s === "PENDING")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-travel-100 px-2 py-0.5 text-xs font-semibold text-travel-800">
        <Clock className="w-3 h-3" /> {t.cardxStatusPending}
      </span>
    );
  if (s === "DENIED" || s === "REJECTED")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-ink">
        <XCircle className="w-3 h-3" /> {t.cardxStatusDenied}
      </span>
    );
  if (s === "REVOKED")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        <ShieldOff className="w-3 h-3" /> {t.cardxStatusRevoked}
      </span>
    );
  return (
    <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
      {status}
    </span>
  );
}

export const SharedConsentsBox: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const [entries, setEntries] = useState<ConsentEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [medRes, consentRes] = await Promise.allSettled([
          apiClient.get("/med-access/list"),
          apiClient.get("/consent-requests"),
        ]);

        const medItems: ConsentEntry[] =
          medRes.status === "fulfilled"
            ? (medRes.value.data.data as MedAccessToken[]).map((d) => ({
                kind: "med",
                data: d,
              }))
            : [];

        const consentItems: ConsentEntry[] =
          consentRes.status === "fulfilled"
            ? (consentRes.value.data.data as ConsentRequest[]).map((d) => ({
                kind: "consent",
                data: d,
              }))
            : [];

        // Sort newest first
        const all = [...medItems, ...consentItems].sort((a, b) => {
          const dateA = a.kind === "med" ? a.data.created_at : a.data.createdAt;
          const dateB = b.kind === "med" ? b.data.created_at : b.data.createdAt;
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        });

        setEntries(all);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="overflow-hidden rounded-[22px] bg-card">
      <div className="flex items-baseline justify-between px-5 pt-5">
        <h2 className="font-display text-lg font-bold text-ink">
          {t.cardxSharedData}
        </h2>
        <button
          onClick={() => navigate("/traveler/card/shares")}
          className="flex items-center gap-1 text-sm font-semibold text-primary-700 hover:underline"
        >
          {t.viewAll} <ExternalLink className="w-3 h-3" />
        </button>
      </div>

      {loading ? (
        <div className="px-4 py-6 flex justify-center">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
        </div>
      ) : entries.length === 0 ? (
        <div className="px-5 py-6 text-center text-sm text-muted-foreground">
          {t.cardxNoSharedData}
        </div>
      ) : (
        <div className="mt-2 divide-y divide-border">
          {entries.slice(0, 8).map((entry) => {
            if (entry.kind === "med") {
              const d = entry.data;
              const date = fmt.date(d.created_at, "medium");
              return (
                <div
                  key={`med-${d.id}`}
                  className="flex items-center gap-3 px-5 py-3"
                >
                  <div className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-health-50">
                    <Stethoscope className="h-[18px] w-[18px] text-health-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-[15px] font-semibold text-ink">
                      {d.doctor_name ?? t.cardxUnknownDoctor}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t.cardxMedAccessLabel} · {date}
                      {d.doctor_email && ` · ${d.doctor_email}`}
                    </div>
                  </div>
                  {statusBadge(d.consent_status, t)}
                </div>
              );
            } else {
              const d = entry.data;
              const date = fmt.date(d.createdAt, "medium");
              return (
                <div
                  key={`consent-${d.id}`}
                  className="flex items-center gap-3 px-5 py-3"
                >
                  <div className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-primary-50">
                    <Stethoscope className="h-[18px] w-[18px] text-primary-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-[15px] font-semibold text-ink">
                      {d.requesterName}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {d.requesterRole}
                      {d.requesterInstitution
                        ? ` · ${d.requesterInstitution}`
                        : ""}
                      {" · "}
                      {date}
                    </div>
                  </div>
                  {statusBadge(d.status, t)}
                </div>
              );
            }
          })}
        </div>
      )}
    </div>
  );
};
